// W45.0 `ADR-0115` — the two-level breadcrumb.
//
// This is the **generalised** form of the back affordance W45.0 first shipped as
// "every non-root page gets a `ChevronLeft` in the eyebrow", and it absorbs it:
// **a breadcrumb's first segment is the back link.** It also carries the
// hierarchy, which a lone back chevron cannot.
//
// Why it had to happen: an audit of the six detail pages found **six different
// return treatments** —
//
//   DiaryDetail          PageHero.eyebrow  → <Link> + ArrowLeft
//   CalendarEventDetail  PageHero.eyebrow  → <Link> + ArrowLeft (preventDefault)
//   anniversaries/[id]   PageHero.eyebrow  → <Link> + ChevronLeft
//   AlbumDetail          PageHero.trailing → circular ChevronLeft
//   WishDetail           PageHero.trailing → ArrowLeft chip
//   PairSettings         **none at all** — the eyebrow is the literal text
//
// …plus four trash bins whose only link was a sentence of body copy pinned to
// the **bottom** of the list, below the fold on any bin with content. One
// component replaces all seven, and it is the version that can say 「你在哪一層」.

import type { MouseEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils/cn";

export interface BreadcrumbCrumb {
  /** Absolute route. Omitted on the last crumb — the current page is not a link. */
  to?: string;
  label: string;
  /**
   * Carried onto the rendered parent link.
   *
   * Not decoration: three pages already published a `data-testid` on their
   * bespoke back link (`back-to-list`, `anniversary-back`, `calendar-event-back`)
   * and `probe-back-affordances.mjs` plus the e2e specs reach those ids. The
   * affordance moved from a hand-written eyebrow link to this component, and
   * without a pass-through every one of those selectors would silently stop
   * matching — the same failure shape as `ADR-0110`'s `[data-component=
   * 'HeroSection']`, which waited on an attribute that never existed.
   */
  testId?: string;
  /**
   * Overrides the link's own navigation, for a page whose "up" is **not** the
   * href.
   *
   * `CalendarEventDetail` is the live case: its parent is `/calendar`, but the
   * back action returns to the *month the event lives in*
   * (`/calendar?y=…&m=…`) so the user lands on context rather than on whatever
   * month they last browsed. That is a real behaviour with a real reason, so it
   * is expressed here rather than by keeping a second, competing back control.
   */
  onSelect?: (event: MouseEvent) => void;
}

export interface BreadcrumbsProps {
  /** Root first, current page last. The component decides which one is current. */
  crumbs: readonly BreadcrumbCrumb[];
  className?: string;
  /** Rendered after the trail — used by pages whose eyebrow carries something else. */
  trailing?: ReactNode;
}

/**
 * Two levels, by decision.
 *
 * The app's deepest route is three segments (`/settings/pair/dissolve`,
 * `/wishes/:id/edit-layout`), so a third level would fold into the current page.
 * That is a trade the reviewer accepted, recorded here so that 「只有兩層」 reads
 * as a decision rather than an oversight. There is no `…` overflow form, and the
 * stylesheet carries no rule for one.
 */
export function Breadcrumbs({ crumbs, className, trailing }: BreadcrumbsProps) {
  if (crumbs.length === 0) return null;
  // A caller passing a 4+ segment trail gets its tail collapsed rather than
  // rendered: a wrapped trail becomes two lines and pushes the title down, and
  // the alternative — rendering all of them — is the thing that was declined.
  const shown = crumbs.length > 2 ? [crumbs[0]!, crumbs[crumbs.length - 1]!] : crumbs;

  return (
    <nav
      data-component="Breadcrumbs"
      data-testid="breadcrumbs"
      aria-label="麵包屑導覽"
      className={cn(
        // 32px of visual height carrying 44px targets. The painted row and the
        // tap area are two different things — the separation this codebase has
        // now made three times (nav-tile/nav-item, shell-plus, and here).
        "flex min-h-8 items-center gap-0.5",
        className,
      )}
    >
      {shown.map((crumb, i) => {
        const isLast = i === shown.length - 1;
        const collapsed = crumbs.length > 2 && i === 1;
        return (
          <span key={`${crumb.label}-${i}`} className="flex min-w-0 items-center gap-0.5">
            {i > 0 ? (
              <ChevronRight
                size={12}
                strokeWidth={2.5}
                aria-hidden
                className="shrink-0 text-ink-3/60"
              />
            ) : null}
            {collapsed ? (
              // The `…` here is the *tail* of a collapsed trail, not a third
              // level. It is a real 44px target like any other segment, because a
              // collapsed trail must not degrade into 20px text runs.
              <span
                className="text-meta px-1.5 font-semibold text-ink-2"
                data-testid="breadcrumb-overflow"
                aria-hidden
              >
                …
              </span>
            ) : isLast ? (
              // **The current page is not a link.** A link to itself is a control
              // that does nothing — the defect class this whole round is about.
              <span
                aria-current="page"
                data-testid="breadcrumb-current"
                className="truncate px-1.5 text-meta font-bold text-ink-1"
              >
                {crumb.label}
              </span>
            ) : (
              <Link
                to={crumb.to ?? "/"}
                data-testid={crumb.testId ?? "breadcrumb-parent"}
                aria-label={`返回${crumb.label}`}
                onClick={crumb.onSelect}
                // `min-h-11 min-w-11` is the touch target; the visual row is
                // 32px tall and the glyph is as wide as the label. That
                // separation is the one W39.86 had to fix by hand on three
                // separate pages — each measured 45×18 and 58×18 because the
                // class string was missing — and which is now impossible to get
                // wrong because the class is declared once, here.
                //
                // `min-w-11` was missing until W46.0. `min-h-11` alone gave a
                // target that was 44 tall and **38.3 wide** on the short labels
                // (`心願`, `日記`), measured across 4 viewports. The comment
                // above claimed the target was handled; it had only claimed the
                // *height* was. Same one-word omission as `.shell-btn`, and it
                // only became visible once something measured a real box.
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg px-1.5 text-meta font-semibold uppercase tracking-wider text-ink-3 transition-colors duration-quick press hover:bg-black/5 hover:text-ink-1"
              >
                {crumb.label}
              </Link>
            )}
          </span>
        );
      })}
      {trailing ? <span className="ml-auto">{trailing}</span> : null}
    </nav>
  );
}

/**
 * A single-parent trail, which is the common case: `心願 › 京都楓葉`.
 *
 * A separate entry point rather than a flag, so the common call site is one
 * import and two strings and cannot be assembled the wrong way round.
 */
export function BreadcrumbParent({
  parentTo,
  parentLabel,
  current,
  className,
  parentTestId,
}: {
  parentTo: string;
  parentLabel: string;
  current: string;
  className?: string;
  /**
   * W52.0 D-9 — the `testId` the parent segment publishes.
   *
   * Not optional decoration. `CalendarCycleView` carried
   * `data-testid="calendar-cycle-back"` and `probe-back-affordances.mjs` plus
   * the e2e specs reach that id; without a pass-through, moving the control
   * into the trail would make every one of those selectors stop matching and
   * the audit would go quiet on a live screen. Same shape as `BreadcrumbCrumb.testId`.
   */
  parentTestId?: string;
}) {
  return (
    <Breadcrumbs
      className={className}
      crumbs={[
        { to: parentTo, label: parentLabel, testId: parentTestId },
        { label: current },
      ]}
    />
  );
}
